import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Logo } from "@/components/intel/Logo";
import { Button } from "@/components/ui/button";
import { TIERS, TIER_ORDER } from "@/lib/payments/tiers";
import { usePaddleCheckout } from "@/hooks/usePaddleCheckout";
import { useAuth } from "@/lib/auth/AuthContext";
import { useSubscription } from "@/hooks/useSubscription";
import { Check, X, ArrowRight } from "lucide-react";
import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";

const AI_PACKS = [
  { priceId: "ai_pack_50_once", label: "50 messages", usd: 9, desc: "Top up when you hit your plan cap." },
  { priceId: "ai_pack_250_once", label: "250 messages", usd: 29, desc: "Best for heavy casework." },
  { priceId: "ai_pack_1000_once", label: "1000 messages", usd: 79, desc: "Bulk pack for teams.", highlight: true },
];

export const Route = createFileRoute("/pricing")({
  head: () => ({
    meta: [
      { title: "Pricing · CARIO INTEL" },
      { name: "description", content: "Three plans for OSINT investigators. Operator, Analyst, and Command tiers — pick what fits your casework." },
      { property: "og:title", content: "CARIO INTEL — Pricing" },
      { property: "og:description", content: "Operator $29 · Analyst $149 · Command $599. Pay monthly, cancel anytime." },
    ],
  }),
  component: PricingPage,
});

function PricingPage() {
  const { user, session } = useAuth();
  const { openCheckout, loading } = usePaddleCheckout();
  const sub = useSubscription();
  const navigate = useNavigate();

  async function pick(priceId: string) {
    if (!session) {
      navigate({ to: "/signup", search: { plan: priceId } as any });
      return;
    }
    await openCheckout({ priceId, customerEmail: user?.email ?? undefined, userId: user?.id });
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <PaymentTestModeBanner />
      <header className="sticky top-0 z-40 border-b border-panel-border bg-background/70 backdrop-blur-xl">
        <div className="mx-auto flex h-14 max-w-7xl items-center px-4 sm:px-6">
          <Link to="/"><Logo /></Link>
          <div className="ml-auto flex items-center gap-3 text-sm">
            {session ? (
              <Link to="/dashboard" className="text-muted-foreground hover:text-foreground">Open console →</Link>
            ) : (
              <>
                <Link to="/login" className="text-muted-foreground hover:text-foreground">Sign in</Link>
                <Link to="/signup" className="text-cyan hover:text-foreground">Create account</Link>
              </>
            )}
          </div>
        </div>
      </header>

      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24 text-center">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan">// Operator-grade pricing</p>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
            One platform. <span className="text-cyan text-glow">Three operating tiers.</span>
          </h1>
          <p className="mt-5 mx-auto max-w-2xl text-base text-muted-foreground">
            Every plan starts with a 1-day free trial. A valid card is required to start the trial; cancel before it ends and you won't be charged. All payments handled securely by our merchant of record.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-6 pb-24">
        <div className="grid gap-6 lg:grid-cols-3">
          {TIER_ORDER.map((slug) => {
            const t = TIERS[slug];
            const isCurrent = sub.productId === t.slug;
            return (
              <div
                key={t.slug}
                className={`relative flex flex-col rounded-2xl p-7 ${
                  t.highlighted
                    ? "glass-strong ring-2 ring-cyan/60 ring-glow"
                    : "glass border border-panel-border"
                }`}
              >
                {t.highlighted && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-cyan text-background text-[10px] font-mono uppercase tracking-[0.25em] px-3 py-1">
                    Most popular
                  </div>
                )}
                <h2 className="font-display text-2xl">{t.name}</h2>
                <p className="mt-1 text-sm text-muted-foreground">{t.tagline}</p>
                <div className="mt-6 flex items-baseline gap-1">
                  <span className="font-display text-5xl">${t.monthlyUsd}</span>
                  <span className="text-sm text-muted-foreground">/month</span>
                </div>
                <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.25em] text-cyan">
                  1-day free trial · card required · cancel anytime
                </p>
                <ul className="mt-6 space-y-2.5 text-sm">
                  {t.features.map((f) => (
                    <li key={f.label} className="flex items-start gap-2">
                      {f.included ? (
                        <Check className="h-4 w-4 text-cyan mt-0.5 flex-shrink-0" />
                      ) : (
                        <X className="h-4 w-4 text-muted-foreground/50 mt-0.5 flex-shrink-0" />
                      )}
                      <span className={f.included ? "" : "text-muted-foreground/60 line-through"}>{f.label}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-7">
                  <Button
                    className="w-full"
                    variant={t.highlighted ? "default" : "outline"}
                    disabled={loading || isCurrent}
                    onClick={() => pick(t.priceId)}
                  >
                    {isCurrent ? "Current plan" : (
                      <>Start free trial <ArrowRight className="h-4 w-4 ml-1" /></>
                    )}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        <p className="mt-10 text-center text-xs text-muted-foreground">
          Need a custom enterprise deployment, SSO, or air-gapped install? <Link to="/get-software" className="text-cyan hover:underline">Talk to us →</Link>
        </p>

        <div className="mt-16">
          <div className="text-center">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan">// AI top-ups</p>
            <h2 className="mt-3 font-display text-3xl">Out of AI messages?</h2>
            <p className="mt-3 mx-auto max-w-xl text-sm text-muted-foreground">
              One-time message packs that stack on top of your plan. They never expire and are consumed automatically when you hit your monthly cap.
            </p>
          </div>
          <div className="mt-8 grid gap-4 sm:grid-cols-3">
            {AI_PACKS.map((p) => (
              <div
                key={p.priceId}
                className={`flex flex-col rounded-xl p-6 ${p.highlight ? "glass-strong ring-1 ring-cyan/50" : "glass border border-panel-border"}`}
              >
                <div className="font-display text-xl">{p.label}</div>
                <div className="mt-1 text-xs text-muted-foreground">{p.desc}</div>
                <div className="mt-4 flex items-baseline gap-1">
                  <span className="font-display text-3xl">${p.usd}</span>
                  <span className="text-xs text-muted-foreground">one-time</span>
                </div>
                <Button
                  className="mt-5 w-full"
                  variant={p.highlight ? "default" : "outline"}
                  disabled={loading}
                  onClick={() => pick(p.priceId)}
                >
                  Buy pack <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            ))}
          </div>
          {!session && (
            <p className="mt-4 text-center text-xs text-muted-foreground">
              You'll be asked to sign in before checkout.
            </p>
          )}
        </div>

        <div className="mt-6 flex justify-center gap-5 text-xs text-muted-foreground">
          <Link to="/terms" className="hover:text-foreground">Terms</Link>
          <Link to="/privacy" className="hover:text-foreground">Privacy</Link>
          <Link to="/refund-policy" className="hover:text-foreground">Refund Policy</Link>
        </div>
      </section>
    </div>
  );
}